import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Globe, Users, UserCheck, X, ChevronDown, Check } from 'lucide-react';
import { useGroups } from '@/hooks/useGroups';
import { functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import type { LabOSUser } from '@/hooks/useLabOSUsers';

export interface WorkflowAudience {
  type: 'everyone' | 'specific' | 'groups';
  userIds?: string[];
  groupIds?: string[];
}

interface WorkflowAudienceSelectorProps {
  audience: WorkflowAudience;
  onChange: (audience: WorkflowAudience) => void;
  ownerId?: string; // Workflow owner UID - always visible in Specific People
}

export default function WorkflowAudienceSelector({ audience, onChange, ownerId }: WorkflowAudienceSelectorProps) {
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const { groups } = useGroups();

  // Lazy load users only when "Specific People" is selected
  const [users, setUsers] = useState<LabOSUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);

  // Fetch users only when audience type is "specific" and we haven't fetched yet
  useEffect(() => {
    if (audience.type === 'specific' && !usersFetched && !usersLoading) {
      const fetchUsers = async () => {
        setUsersLoading(true);
        try {
          const adminListUsers = httpsCallable<Record<string, never>, LabOSUser[]>(functions, 'adminListUsers');
          const result = await adminListUsers({});
          setUsers(result.data || []);
          setUsersFetched(true);
        } catch (err) {
          console.error('[WorkflowAudienceSelector] Error fetching users:', err);
          setUsers([]);
        } finally {
          setUsersLoading(false);
        }
      };
      fetchUsers();
    }
  }, [audience.type, usersFetched, usersLoading]);

  const handleTypeChange = (type: 'everyone' | 'specific' | 'groups') => {
    if (type === 'everyone') {
      onChange({ type: 'everyone' });
    } else if (type === 'specific') {
      // Always include owner when switching to specific
      const userIds = ownerId ? [ownerId, ...(audience.userIds || []).filter((id) => id !== ownerId)] : audience.userIds || [];
      onChange({ type: 'specific', userIds });
    } else {
      onChange({ type: 'groups', groupIds: audience.groupIds || [] });
    }
  };

  const handleAddUser = (userId: string) => {
    if (!userId) return;
    const userIds = [...(audience.userIds || []), userId];
    onChange({ ...audience, userIds });
    setShowUserDropdown(false);
  };

  const handleRemoveUser = (userId: string) => {
    // Prevent removing the owner
    if (ownerId && userId === ownerId) {
      return;
    }
    const userIds = (audience.userIds || []).filter((id) => id !== userId);
    onChange({ ...audience, userIds });
  };

  const handleAddGroup = (groupId: string) => {
    if (!groupId) return;
    const groupIds = [...(audience.groupIds || []), groupId];
    onChange({ ...audience, groupIds });
    setShowGroupDropdown(false);
  };

  const handleRemoveGroup = (groupId: string) => {
    const groupIds = (audience.groupIds || []).filter((id) => id !== groupId);
    onChange({ ...audience, groupIds });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Workflow Audience</CardTitle>
        <CardDescription>Control who can view and execute this workflow</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Audience Type Selection */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Visibility</label>
          <div className="grid gap-2">
            <button
              type="button"
              onClick={() => handleTypeChange('specific')}
              className={
                'flex items-center gap-3 p-3 rounded-lg border transition-all text-left ' +
                (audience.type === 'specific'
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-500/10'
                  : 'border-border/60 hover:border-border hover:bg-accent/50')
              }
            >
              <UserCheck className={'h-5 w-5 ' + (audience.type === 'specific' ? 'text-violet-600' : 'text-muted-foreground')} />
              <div className="flex-1">
                <div className="font-medium text-sm">Specific People</div>
                <div className="text-xs text-muted-foreground">Only selected users can access</div>
              </div>
              {audience.type === 'specific' && <Badge variant="default">Active</Badge>}
            </button>

            <button
              type="button"
              onClick={() => handleTypeChange('groups')}
              className={
                'flex items-center gap-3 p-3 rounded-lg border transition-all text-left ' +
                (audience.type === 'groups'
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-500/10'
                  : 'border-border/60 hover:border-border hover:bg-accent/50')
              }
            >
              <Users className={'h-5 w-5 ' + (audience.type === 'groups' ? 'text-violet-600' : 'text-muted-foreground')} />
              <div className="flex-1">
                <div className="font-medium text-sm">Named Groups</div>
                <div className="text-xs text-muted-foreground">Users with specific roles/groups</div>
              </div>
              {audience.type === 'groups' && <Badge variant="default">Active</Badge>}
            </button>

            <button
              type="button"
              onClick={() => handleTypeChange('everyone')}
              className={
                'flex items-center gap-3 p-3 rounded-lg border transition-all text-left ' +
                (audience.type === 'everyone'
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-500/10'
                  : 'border-border/60 hover:border-border hover:bg-accent/50')
              }
            >
              <Globe className={'h-5 w-5 ' + (audience.type === 'everyone' ? 'text-violet-600' : 'text-muted-foreground')} />
              <div className="flex-1">
                <div className="font-medium text-sm">Everyone</div>
                <div className="text-xs text-muted-foreground">All authenticated users can access</div>
              </div>
              {audience.type === 'everyone' && <Badge variant="default">Active</Badge>}
            </button>
          </div>
        </div>

        {/* Specific Users Management */}
        {audience.type === 'specific' && (
          <div className="space-y-3 pt-2">
            <label className="text-xs font-medium text-muted-foreground">Allowed Users</label>
            <div className="space-y-2">
              {/* Available Users Dropdown */}
              {users.length > 0 && (
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowUserDropdown(!showUserDropdown);
                      setUserSearchQuery('');
                    }}
                    className="w-full justify-between"
                    disabled={usersLoading}
                  >
                    <span className="text-xs">{usersLoading ? 'Loading users...' : 'Select from LabOS users'}</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                  {showUserDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
                      <div className="sticky top-0 bg-background p-2 border-b">
                        <Input
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          placeholder="Search by name, email or UID..."
                          className="text-sm h-8"
                          autoFocus
                        />
                      </div>
                      {(() => {
                        const query = userSearchQuery.trim().toLowerCase();
                        const filteredUsers = query
                          ? users.filter(
                              (user) =>
                                (user.display_name || '').toLowerCase().includes(query) ||
                                user.email.toLowerCase().includes(query) ||
                                user.uid.toLowerCase().includes(query),
                            )
                          : users;

                        if (filteredUsers.length === 0) {
                          return (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              {users.length === 0 ? 'No users available' : 'No users match your search'}
                            </div>
                          );
                        }

                        return filteredUsers.map((user) => {
                          const isAssigned = (audience.userIds || []).includes(user.uid);
                          return (
                            <button
                              key={user.uid}
                              type="button"
                              onClick={() => {
                                if (isAssigned) {
                                  handleRemoveUser(user.uid);
                                } else {
                                  handleAddUser(user.uid);
                                }
                              }}
                              className={
                                'w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-start gap-2 ' +
                                (isAssigned ? 'bg-violet-50 dark:bg-violet-500/10' : '')
                              }
                            >
                              <div className="flex-shrink-0 mt-0.5">
                                {isAssigned && (
                                  <div className="w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                )}
                                {!isAssigned && <div className="w-4 h-4 rounded-full border-2 border-border" />}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium">{user.display_name || user.email}</div>
                                <div className="text-xs text-muted-foreground">{user.email}</div>
                                <div className="text-xs text-muted-foreground font-mono mt-1">
                                  UID: {user.uid.substring(0, 12)}...
                                </div>
                              </div>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
            {audience.userIds && audience.userIds.length > 0 ? (
              <div className="space-y-1.5">
                {audience.userIds.map((userId) => {
                  const user = users.find((u) => u.uid === userId);
                  const isOwner = ownerId && userId === ownerId;
                  return (
                    <div
                      key={userId}
                      className={
                        'flex items-center justify-between p-2 rounded-md border ' +
                        (isOwner
                          ? 'bg-violet-50 dark:bg-violet-500/10 border-violet-200 dark:border-violet-500/30'
                          : 'bg-accent/50 border-border/60')
                      }
                    >
                      <div className="flex-1">
                        {user ? (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{user.display_name || user.email}</span>
                              {isOwner && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                                  Owner
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">{user.email}</div>
                            <div className="text-xs text-muted-foreground font-mono">{userId.substring(0, 12)}...</div>
                          </>
                        ) : (
                          <span className="text-sm font-mono text-muted-foreground">{userId}</span>
                        )}
                      </div>
                      {!isOwner && (
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(userId)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No users added yet</p>
            )}
          </div>
        )}

        {/* Groups Management */}
        {audience.type === 'groups' && (
          <div className="space-y-3 pt-2">
            <label className="text-xs font-medium text-muted-foreground">Required Groups/Roles</label>
            <div className="space-y-2">
              {/* Available Groups Dropdown */}
              {groups.length > 0 && (
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowGroupDropdown(!showGroupDropdown);
                      setGroupSearchQuery('');
                    }}
                    className="w-full justify-between"
                  >
                    <span className="text-xs">Select from existing groups</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                  {showGroupDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
                      <div className="sticky top-0 bg-background p-2 border-b">
                        <Input
                          value={groupSearchQuery}
                          onChange={(e) => setGroupSearchQuery(e.target.value)}
                          placeholder="Search by name or description..."
                          className="text-sm h-8"
                          autoFocus
                        />
                      </div>
                      {(() => {
                        const query = groupSearchQuery.trim().toLowerCase();
                        const availableGroups = groups
                          .filter((g) => !(audience.groupIds || []).includes(g.id))
                          .filter(
                            (g) =>
                              !query ||
                              g.name.toLowerCase().includes(query) ||
                              (g.description || '').toLowerCase().includes(query),
                          );

                        if (availableGroups.length === 0) {
                          const noneLeft = groups.filter((g) => !(audience.groupIds || []).includes(g.id)).length === 0;
                          return (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              {noneLeft ? 'All groups already added' : 'No groups match your search'}
                            </div>
                          );
                        }

                        return availableGroups.map((group) => (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => handleAddGroup(group.id)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                          >
                            <div className="font-medium">{group.name}</div>
                            {group.description && <div className="text-xs text-muted-foreground">{group.description}</div>}
                            <div className="text-xs text-muted-foreground mt-1">
                              {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                            </div>
                          </button>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
            {audience.groupIds && audience.groupIds.length > 0 ? (
              <div className="space-y-1.5">
                {audience.groupIds.map((groupId) => {
                  const group = groups.find((g) => g.id === groupId);
                  return (
                    <div
                      key={groupId}
                      className="flex items-center justify-between p-2 bg-accent/50 rounded-md border border-border/60"
                    >
                      <div className="flex-1">
                        <span className="text-sm font-medium">{group?.name || groupId}</span>
                        {group && (
                          <div className="text-xs text-muted-foreground">
                            {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveGroup(groupId)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No groups added yet</p>
            )}
            <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-md p-3 mt-3">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                💡 Users will have access if they have <strong>any</strong> of the specified groups/roles.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
